import { AuthenticationProvider } from "@/common/providers/authentication";
import { notifyError, notifySuccess } from "@/components/ui/sonner";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import * as AuthenticationServices from "../services/authentication";
import useHttpProvider from "@/common/hooks/use-http";
import { HomeRoute } from "@/features/home/route";
import { ResponseError } from "@/common/models/error";

const passwordPattern = /^(?=.*\d).{8,}$/;

const FormSchema = z
	.object({
		temporaryPassword: z.string({ message: "Temporary password is required" }),
		newPassword: z
			.string({ message: "New password is required" })
			.min(8, "Password must be at least 8 characters long")
			.regex(passwordPattern, "Password must contain at least one number"),
		confirmPassword: z
			.string({ message: "Confirm password is required" })
			.min(8, "Password must be at least 8 characters long"),
	})
	.superRefine((data, ctx) => {
		if (data.newPassword !== data.confirmPassword) {
			ctx.addIssue({
				code: "custom",
				message: "Passwords do not match",
				path: ["confirmPassword"],
			});
		}
		if (data.temporaryPassword === data.newPassword) {
			ctx.addIssue({
				code: "custom",
				message: "New password cannot be the same as the temporary password",
				path: ["newPassword"],
			});
		}
	});

function ResetRequiredPage() {
	const { http } = useHttpProvider();
	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {},
	});

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		setIsSubmitting(true);
		try {
			const response = await AuthenticationServices.changePassword(
				http,
				data.temporaryPassword,
				data.newPassword
			);
			await AuthenticationProvider.signin(response);
			notifySuccess("Password updated", "Your new password has been set. Welcome!");
			navigate(HomeRoute);
		} catch (error) {
			const detail = (error as ResponseError).detail;
			if (detail === "invalid_password") {
				notifyError(
					"Invalid temporary password",
					"The temporary password you entered is incorrect."
				);
				form.setError("temporaryPassword", {
					type: "manual",
					message: "Incorrect temporary password",
				});
			} else {
				notifyError("Error", "Something went wrong. Please try again.");
			}
		} finally {
			setIsSubmitting(false);
		}
	}

	return (
		<div className="flex min-h-screen items-center justify-center px-4">
			<div className="w-full max-w-md space-y-8">
				<div className="space-y-2 text-center">
					<h1 className="web-heading-h3-medium">Set a new password</h1>
					<p className="web-body-b2 text-gray-500">
						A temporary password was emailed to you. Enter it below along with
						the new password you'd like to use.
					</p>
				</div>

				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="flex flex-col gap-6"
					>
						<FormField
							control={form.control}
							name="temporaryPassword"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Temporary Password</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											type="password"
											placeholder="Paste the password from your email"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="newPassword"
							render={({ field }) => (
								<FormItem>
									<FormLabel>New Password</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											type="password"
											placeholder="New password"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="confirmPassword"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Confirm New Password</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											type="password"
											placeholder="Confirm new password"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button type="submit" disabled={isSubmitting} className="w-full">
							{isSubmitting ? "Updating..." : "Set New Password"}
						</Button>
					</form>
				</Form>
			</div>
		</div>
	);
}

export default ResetRequiredPage;
